import type { DocNode } from '@atlaskit/adf-schema/doc';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type {
	ConfluenceCreatePageCommandProperties,
	GeneratedVisualMetadata,
} from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-create-page-command';

import type { DocumentCreateCommandProcessor } from '../stream-processors/create-command-processor';
import { extractCwrRemixResults } from '../stream-processors/extract-cwr-remix-results';
import type { AbortReason } from '../types/abort-reason';
import {
	getCwrRemixResultInstrumentationId,
	type CwrRemixVisualMetadata,
} from '../types/cwr-remix-result';
import type { PassThroughCommandProps } from './command-handler';
import { StreamingCommandHandler } from './streaming-command-handler';

/**
 * Whether this payload's `adf` should replace the accumulated stream content.
 *
 * In certain scenarios we defer generated visuals (images, MAUI apps) to the action-complete payload and
 * mark it with `applyAdf: true`, meaning its `adf` is the complete document rather than a delta.
 */
function shouldApplyFinalAdf(payload: ConfluenceCreatePageCommandProperties): boolean {
	if (!payload.isStreamComplete) {
		return false;
	}
	// Same gate evaluated on convo-ai_server and confluence_web.
	if (!fg('aifc_page_create_defer_generated_visuals')) {
		return false;
	}
	if (payload.data.applyAdf !== true) {
		return false;
	}
	// Guards against a delta chunk being swapped in as if it were the whole document.
	return payload.data.adf.trimStart().startsWith('{"type":"doc"');
}

const isNonEmptyString = (value: unknown): value is string =>
	typeof value === 'string' && value.length > 0;

const getVisualMetadataByResultId = (
	generatedVisualMetadata: GeneratedVisualMetadata[] | undefined,
): ReadonlyMap<string, CwrRemixVisualMetadata> | undefined => {
	const visualMetadataByResultId = new Map<string, CwrRemixVisualMetadata>();
	for (const metadata of generatedVisualMetadata ?? []) {
		if (!isNonEmptyString(metadata.resultId) || !isNonEmptyString(metadata.dynamicUiType)) {
			continue;
		}
		visualMetadataByResultId.set(metadata.resultId, {
			dynamicUiType: metadata.dynamicUiType,
			...(isNonEmptyString(metadata.dynamicUiSubtype) && {
				dynamicUiSubtype: metadata.dynamicUiSubtype,
			}),
		});
	}
	return visualMetadataByResultId.size > 0 ? visualMetadataByResultId : undefined;
};

/**
 * Handles the streaming/final-ADF lifecycle of an AIFC "create page" command: streams incoming
 * ADF chunks into the editor, swaps in the terminal payload's own ADF when the backend has
 * deferred generated visuals to it, and writes the finished document on completion.
 */
export class CreateCommandHandler extends StreamingCommandHandler<ConfluenceCreatePageCommandProperties> {
	private cwrCreateSingleInstrumentationId: string | undefined;
	private cwrCreateExperience: ConfluenceCreatePageCommandProperties['cwrCreateExperience'];
	private cwrCreateGeneratedVisualMetadata: GeneratedVisualMetadata[] | undefined;
	constructor(
		props: PassThroughCommandProps,
		private createCommandProcessor: DocumentCreateCommandProcessor,
	) {
		super(props, 'create');
	}

	process(payload: ConfluenceCreatePageCommandProperties): void {
		if (!payload.data.adf) {
			return;
		}

		// Record (shape only) the receiver lifecycle facts at command arrival,
		// before any replay/init mutates processor state. No-op unless the debug
		// gate is enabled; the processor stores only the first arrival per stream.
		const previousAccumulatedData = payload.data.previousAccumulatedData;
		// `initialised.collabInitialisedAt` reflects an active collab session, unlike
		// plugin-presence checks such as `!!editorAPI.collabEdit`, which are true as soon as the
		// plugin is registered but before the document has loaded over NCS. See isLateDuplicate
		// below for why this needs to be detected at all.
		const isCollabBacked = Boolean(
			this.props.editorAPI.collabEdit?.sharedState.currentState()?.initialised?.collabInitialisedAt,
		);
		const willApplyFinalAdf = shouldApplyFinalAdf(payload);
		// A terminal payload has nothing left to stream, so replaying here would only clear the
		// document and rebuild it from `previousAccumulatedData` -- prior parts only, dropping
		// whatever the terminal payload carries. Under collab that clear syncs, discarding what
		// finish() or a backend override-document already wrote.
		const isLateDuplicate = isCollabBacked && payload.isStreamComplete;

		this.createCommandProcessor.markCommandArrived({
			processorWasInitializedWhenCommandArrived: this.createCommandProcessor.isInitialized(),
			previousAccumulatedDataPresent: !!previousAccumulatedData,
			previousAccumulatedDataStartsWithDocEnvelope: previousAccumulatedData
				? previousAccumulatedData.trimStart().startsWith('{"type":"doc"')
				: undefined,
			isCollabBackedWhenCommandArrived: isCollabBacked,
			isStreamCompleteWhenCommandArrived: payload.isStreamComplete,
			willApplyFinalAdf,
		});
		if (payload.isStreamComplete) {
			this.cwrCreateSingleInstrumentationId = payload.singleInstrumentationId;
			this.cwrCreateExperience = payload.cwrCreateExperience;
			this.cwrCreateGeneratedVisualMetadata = payload.data.generatedVisualMetadata;
		}

		if (
			!this.createCommandProcessor.isInitialized() &&
			payload.data.previousAccumulatedData &&
			!isLateDuplicate &&
			!willApplyFinalAdf
		) {
			// The scenario where the command processor is not initialized but the payload contains
			// accumulated ADF can happen if the user closes and reopens the modal during streaming.
			// To handle this, we first clear the document (in case it first streamed for long enough
			// to trigger a draft sync) and then stream in the accumulated ADF so that streaming
			// subsequent chunks can continue correctly.
			this.props.editorAPI.core.actions.replaceDocument('', {
				addToHistory: false,
				scrollIntoView: false,
			});
			// Record (shape only) that this stream is a replay of previously
			// accumulated data, so blank-page debug telemetry can attribute
			// failures to the modal close/reopen mid-stream path.
			this.createCommandProcessor.markPreviousAccumulatedDataReplay(
				payload.data.previousAccumulatedData,
			);
			this.createCommandProcessor.stream(
				payload.data.previousAccumulatedData,
				false,
				typeof payload.traceId === 'string' ? payload.traceId : undefined,
				payload.singleInstrumentationId,
			);
		}

		if (!payload.isStreamComplete) {
			this.createCommandProcessor.stream(
				payload.data.adf,
				this.isStreamFinished,
				typeof payload.traceId === 'string' ? payload.traceId : undefined,
				payload.singleInstrumentationId,
			);
		} else if (willApplyFinalAdf) {
			// AIFC generates images/MAUI apps asynchronously and, under this gate, emits them only
			// in the terminal payload -- the streamed action parts accumulated so far are an
			// incomplete document. Swap the accumulation for this payload's ADF so finish() parses
			// and writes the complete document instead.
			this.createCommandProcessor.replaceAccumulatedWithFinalAdf(payload.data.adf);
		}
	}

	async finishAction(): Promise<DocNode | undefined> {
		const adfDoc = await this.createCommandProcessor.finish({
			onError: this.props.onStreamError,
			onRepair: this.props.onStreamRepair,
		});
		const cwrCreateSingleInstrumentationId = this.cwrCreateSingleInstrumentationId;
		const cwrCreateExperience = this.cwrCreateExperience;
		const cwrCreateGeneratedVisualMetadata = this.cwrCreateGeneratedVisualMetadata;
		this.cwrCreateSingleInstrumentationId = undefined;
		this.cwrCreateExperience = undefined;
		this.cwrCreateGeneratedVisualMetadata = undefined;

		if (adfDoc) {
			this.props.editorAPI.core.actions.replaceDocument(adfDoc, {
				addToHistory: false,
				scrollIntoView: false,
			});
			// Ensure all expands are open
			this.props.editorAPI.core.actions.execute(
				this.props.editorAPI.expand?.commands.toggleExpandRange(),
			);
			this.trackCwrCreateRemixResults(
				adfDoc,
				cwrCreateSingleInstrumentationId,
				cwrCreateExperience,
				cwrCreateGeneratedVisualMetadata,
			);
		}

		return adfDoc;
	}

	private trackCwrCreateRemixResults(
		adfDoc: DocNode,
		singleInstrumentationId: string | undefined,
		cwrCreateExperience: ConfluenceCreatePageCommandProperties['cwrCreateExperience'],
		generatedVisualMetadata: GeneratedVisualMetadata[] | undefined,
	): void {
		if (
			!isExperimentEnabled('platform_editor_cwr_remix_acceptance') ||
			!singleInstrumentationId ||
			!cwrCreateExperience
		) {
			return;
		}

		// A page-create document starts blank, so every media node is newly generated content.
		// Unlike editor updates, these media nodes do not carry a Remix dataConsumer mark.
		const visualMetadataByResultId = getVisualMetadataByResultId(generatedVisualMetadata);
		for (const [resultIndex, result] of extractCwrRemixResults(
			'insertNodeAfter',
			JSON.stringify(adfDoc),
			visualMetadataByResultId,
		).entries()) {
			this.props.onCwrRemixResultApplied?.({
				actionKey: 'aifc-confluence-page-create',
				invokedFrom: 'cwr',
				result,
				singleInstrumentationId: getCwrRemixResultInstrumentationId(
					singleInstrumentationId,
					resultIndex,
				),
			});
		}
	}

	abort(reason?: AbortReason): void {
		this.createCommandProcessor.abort(reason);
	}
}
